import {RenderingServices, utils} from '@wallaby-ui/core';
import {ColDef} from 'ag-grid-community';
import {cloneDeep, filter, find, isArray, map, matchesProperty} from 'lodash';
import {
  CELL_RENDERER_PARAMS_HEIGHT_BY_CONTENT,
  CELL_RENDERER_PARAMS_AG,
  CellRendererParams,
} from 'src/js/model/cell_renderer_params';

export const PROPERTY_TYPE_CASE = 'case';
export const PROPERTY_TYPE_CASE_PROJECT = 'case_project';
export const PROPERTY_TYPE_DOCUMENT = 'document';
export const PROPERTY_TYPE_DOCUMENT_PROJECT = 'document_project';
export const PROPERTY_TYPE_HIERARCHY = 'hierarchy';

export const TABLE_TYPE_RDG = 'RDG';
export const COLUMN_KEY = 'key';
export const COLUMN_NAME = 'name';
export const COLUMN_TOOLTIP = 'tooltip';
export const COLUMN_SORTABLE = 'sortable';
export const COLUMN_WIDTH = 'width';
export const COLUMN_AUTO_HEIGHT = 'autoHeight';
export const COLUMN_HIDE = 'hide';
export const COLUMN_PROPERTY_TYPE = 'propertyType';
export const COLUMN_CHILDREN = 'children';

export const TABLE_TYPE_AG = 'AG';
export const COLUMN_KEY_AG = 'field';
export const COLUMN_NAME_AG = 'headerName';
export const COLUMN_TOOLTIP_AG = 'headerTooltip';
export const COLUMN_STATE_SORT = 'sort';
export const COLUMN_STATE_SORT_INDEX = 'sortIndex';
export const COLUMN_RENDERER_AG = 'cellRenderer';
export const COLUMN_COMPARATOR = 'comparator';

export type TableType = typeof TABLE_TYPE_RDG | typeof TABLE_TYPE_AG;

export class ColumnConfig {
  private readonly rawData: Partial<ColDef>;

  private readonly id: string | number | undefined;

  private readonly tableType: TableType;

  static hasAutoHeightColumn(columns) {
    return !!find(columns, matchesProperty(COLUMN_AUTO_HEIGHT, true));
  }

  static checkColumnHasHeightByContent(columns, columnKey) {
    const column = find(columns, matchesProperty(COLUMN_KEY_AG, columnKey));
    return (
      column &&
      column[CELL_RENDERER_PARAMS_AG] &&
      column[CELL_RENDERER_PARAMS_AG][CELL_RENDERER_PARAMS_HEIGHT_BY_CONTENT]
    );
  }

  static hasHeightByContentColumn(columns) {
    return !!find(
      columns,
      (column) =>
        column[CELL_RENDERER_PARAMS_AG] &&
        column[CELL_RENDERER_PARAMS_AG][CELL_RENDERER_PARAMS_HEIGHT_BY_CONTENT]
    );
  }

  static computeRowHeightByContent(
    params,
    columns,
    columnWidths,
    translateService
  ) {
    let subRows = 1;
    const heightByContentColumns = filter(columns, (column) => {
      const cellRendererParams = new CellRendererParams(
        column[CELL_RENDERER_PARAMS_AG]
      );
      return cellRendererParams.getHeightByContent() && !column[COLUMN_HIDE];
    });
    heightByContentColumns.forEach((column) => {
      let currSubRows = 1;
      const cellRendererParams = new CellRendererParams(
        column[CELL_RENDERER_PARAMS_AG]
      );
      // Warning: keys in columnWidths state is a property "colId", that is not always equal to a property "field"
      // see: https://www.ag-grid.com/javascript-grid-column-definitions/#column-ids
      const columnWidth =
        columnWidths[column[COLUMN_KEY_AG]] -
        cellRendererParams.getValueMargin();
      if (params.data && params.data[column[COLUMN_KEY_AG]] && columnWidth) {
        const font = cellRendererParams.getFont();
        const cellData = params.data[column[COLUMN_KEY_AG]];
        const ownCellData = cellRendererParams.getCellValue(
          cellData,
          translateService
        );
        if (isArray(ownCellData)) {
          let restWidth = columnWidth;
          ownCellData.forEach((dataItem) => {
            const ownDataItem = cellRendererParams.getCellItemValue(
              dataItem,
              translateService
            );
            const elementWidth =
              (utils.StringUtils.measureRenderedTextWidth(ownDataItem, font) +
                cellRendererParams.getItemMargin()) *
              cellRendererParams.getMeasurementError();
            if (restWidth < elementWidth) {
              // data item is wider than the (rest of) column
              if (restWidth === columnWidth) {
                // data item is wider than whole column, it takes the whole row
                restWidth = 0;
              } else {
                // data item is wider than the rest of column, it goes in the next row
                // reset restWidth to columnWidth and subtract elementWidth
                restWidth = columnWidth - elementWidth;
                currSubRows += 1;
              }
            } else {
              // data item is smaller than the rest of column, it goes in the current row
              restWidth -= elementWidth;
            }
          });
        } else {
          const elementWidth =
            utils.StringUtils.measureRenderedTextWidth(ownCellData, font) *
            cellRendererParams.getMeasurementError();
          currSubRows = Math.ceil(elementWidth / columnWidth);
        }
      }
      // set number of subRows as max of necessary subRows from all heightByContentColumns
      subRows = Math.max(subRows, currSubRows);
    });
    return subRows;
  }

  static applyServices(
    columnDefinition,
    services: Partial<RenderingServices> = {}
  ) {
    let transformedChildren;
    if (columnDefinition && columnDefinition[COLUMN_CHILDREN]) {
      const enhancedChildren = map(columnDefinition[COLUMN_CHILDREN], (child) =>
        ColumnConfig.applyServices(child, services)
      );
      transformedChildren = {[COLUMN_CHILDREN]: enhancedChildren};
    }

    const {intl} = services;
    const translatedValues = {};
    const columnName = columnDefinition[COLUMN_NAME_AG];
    const columnTooltip = columnDefinition[COLUMN_TOOLTIP_AG];
    if (intl && columnName) {
      translatedValues[COLUMN_NAME_AG] = utils.TranslateUtils.hasTranslation(
        columnName,
        intl
      )
        ? utils.TranslateUtils.translate(columnName, intl)
        : columnName;
    }
    if (intl && columnTooltip) {
      translatedValues[COLUMN_TOOLTIP_AG] = utils.TranslateUtils.hasTranslation(
        columnTooltip,
        intl
      )
        ? utils.TranslateUtils.translate(columnTooltip, intl)
        : columnTooltip;
    }
    // push translation service to column comparator
    if (columnDefinition[COLUMN_COMPARATOR]) {
      translatedValues[COLUMN_COMPARATOR] = (
        valueA,
        valueB,
        nodeA,
        nodeB,
        isInverted
      ) =>
        // invoke comparator with translation service as sixth parameter
        columnDefinition[COLUMN_COMPARATOR](
          valueA,
          valueB,
          nodeA,
          nodeB,
          isInverted,
          services
        );
    }
    // push translation service to column formatter function
    if (columnDefinition[COLUMN_RENDERER_AG]) {
      translatedValues[COLUMN_RENDERER_AG] = (params) =>
        // invoke formatter with translation service as second and store as third parameter
        columnDefinition[COLUMN_RENDERER_AG](params, services);
    }
    return {
      ...columnDefinition,
      ...translatedValues,
      ...transformedChildren,
    };
  }

  constructor(
    rawData: Partial<ColDef> = {},
    id: string | number | undefined = undefined,
    tableType: TableType = TABLE_TYPE_AG
  ) {
    this.rawData = {...rawData};
    this.id = id;
    this.tableType = tableType;
  }

  clone() {
    return Object.assign(
      new ColumnConfig(cloneDeep(this.rawData), this.id, this.tableType),
      this
    );
  }

  getId() {
    return this.id;
  }

  getRawData() {
    return this.rawData;
  }

  getTableType() {
    return this.tableType;
  }

  getKey() {
    return this.rawData[COLUMN_KEY];
  }

  getName() {
    return this.rawData[COLUMN_NAME];
  }

  getChildren() {
    return this.rawData[COLUMN_CHILDREN];
  }

  getHide() {
    return !!this.rawData[COLUMN_HIDE];
  }

  getSortable() {
    return !!this.rawData[COLUMN_SORTABLE];
  }

  getTooltip() {
    return this.rawData[COLUMN_TOOLTIP];
  }

  getWidth() {
    return this.rawData[COLUMN_WIDTH] || 'auto';
  }

  getPropertyType() {
    return this.rawData[COLUMN_PROPERTY_TYPE];
  }

  hasHeightByContent() {
    return (
      this.rawData[CELL_RENDERER_PARAMS_AG] &&
      this.rawData[CELL_RENDERER_PARAMS_AG][
        CELL_RENDERER_PARAMS_HEIGHT_BY_CONTENT
      ]
    );
  }
}

export default {ColumnConfig};
